import type { RadioProps } from "@nextui-org/react";
import { Chip, Radio } from "@nextui-org/react";
import React, { forwardRef, ReactNode } from "react";

import Trans from "@/components/Trans/Trans";
import { cn } from "@/utils/cn";

export type PaymentMethodRadioProps = RadioProps & {
  icon?: ReactNode;
  label?: string;
  isExpired?: boolean;
  isRecommended?: boolean;
};

const PaymentMethodRadio = forwardRef<HTMLInputElement, PaymentMethodRadioProps>(
  ({ label, children, description, icon, isExpired, isRecommended, classNames = {}, className, ...props }, ref) => {
    return (
      <Radio
        ref={ref}
        {...props}
        classNames={{
          ...classNames,
          base: cn(
            "inline-flex m-0 px-3 py-4 max-w-[100%] items-center justify-between",
            "flex-row-reverse w-full cursor-pointer rounded-lg 3 !border-medium border-default-100",
            "data-[selected=true]:border-primary",
            classNames?.base,
            className,
          ),
          labelWrapper: cn("ml-0", classNames?.labelWrapper),
        }}
        color="primary"
      >
        <div className="flex w-full items-center gap-3">
          <div className="item-center flex rounded-small p-2">{icon}</div>
          <div className="flex w-full flex-col gap-1">
            <div className="flex items-center gap-3">
              <p className="text-small">{label}</p>
              {isExpired ? (
                <Chip className="h-6 p-0 text-tiny" color="danger">
                  <Trans i18nKey="subscription:checkout.paymentMethod.expired" />
                </Chip>
              ) : null}
              {isRecommended ? (
                <Chip className="h-6 p-0 text-tiny" color="success" variant="flat">
                  <Trans i18nKey="subscription:checkout.paymentMethod.recommended" />
                </Chip>
              ) : null}
            </div>
            <p className="text-tiny text-default-400">{description || children}</p>
          </div>
        </div>
      </Radio>
    );
  },
);

PaymentMethodRadio.displayName = "PaymentMethodRadio";

export default PaymentMethodRadio;
